import React from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { alpha } from '@mui/material/styles';
import MicOffIcon from '@mui/icons-material/MicOff';
import MicIcon from '@mui/icons-material/Mic';
import DialpadIcon from '@mui/icons-material/Dialpad';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import CallEndIcon from '@mui/icons-material/CallEnd';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StopCircleIcon from '@mui/icons-material/StopCircle';

import {
  CS_FONT,
  CS_WEIGHT,
  csTokens,
  endInk,
  endRed,
  secondaryText,
} from 'components/CallScribe/callScribeTheme';
import {
  RECORDING_ACTIVE_LABEL,
  RECORDING_STOPPED_LABEL,
  STOPPING_RECORDING_LABEL,
  STOP_FAILED_TEXT,
  STOP_RECORDING_LABEL,
  STOP_RETRY_LABEL,
  type TRecordingControlState,
} from 'components/CallScribe/recordingStop';

const CONTROL_DIAMETER = 60;
const END_BUTTON_DIAMETER = 60;

const useInCallControlsStyles = makeStyles((theme: Theme) => ({
  dock: {
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    width: '100%',
  },
  dockRow: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'minmax(0, 1fr)',
    gap: 10,
  },
  dockButton: {
    height: 60,
    minWidth: 0,
    border: 'none',
    borderRadius: 16,
    background: csTokens(theme).glass,
    boxShadow: `inset 0 0 0 1px ${csTokens(theme).glassBorder}`,
    color: csTokens(theme).onGround,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: 0,
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
    transition: 'background-color 150ms ease-out, transform 120ms ease-out',
    '& svg': { fontSize: 24 },
    '&:hover:not(:disabled)': {
      background: csTokens(theme).glassStrong,
    },
    '&:active:not(:disabled)': {
      transform: 'scale(0.98)',
    },
    '&:focus-visible': {
      outline: `2px solid ${csTokens(theme).stageFocus}`,
      outlineOffset: 2,
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.45,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'background-color 150ms ease-out',
      '&:active:not(:disabled)': { transform: 'none' },
    },
  },
  dockButtonOn: {
    background: csTokens(theme).onGround,
    color: csTokens(theme).ground,
    boxShadow: 'none',
    '&:hover:not(:disabled)': {
      background: csTokens(theme).onGround,
    },
  },
  dockLabel: {
    ...CS_FONT.caption,
    fontWeight: CS_WEIGHT.strong,
  },
  endWide: {
    width: '100%',
    height: 56,
    border: 'none',
    borderRadius: 16,
    background: csTokens(theme).stageEnd,
    color: csTokens(theme).stageEndInk,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: 0,
    fontFamily: 'inherit',
    fontSize: 16,
    lineHeight: '22px',
    fontWeight: CS_WEIGHT.heavy,
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
    transition: 'filter 150ms ease-out, transform 120ms ease-out',
    '& svg': { fontSize: 24 },
    '&:hover': {
      filter: 'brightness(1.06)',
    },
    '&:active': {
      transform: 'scale(0.99)',
    },
    '&:focus-visible': {
      outline: `2px solid ${csTokens(theme).stageFocus}`,
      outlineOffset: 2,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'filter 150ms ease-out',
      '&:active': { transform: 'none' },
    },
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  grid: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center',
  },
  control: {
    width: CONTROL_DIAMETER,
    height: CONTROL_DIAMETER,
    borderRadius: '50%',
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    color: secondaryText(theme),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
    transition: 'background 120ms ease, color 120ms ease',
    '&:hover': {
      background: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.text.primary}`,
      outlineOffset: 2,
    },
  },
  controlActive: {
    background: theme.palette.text.primary,
    color: theme.palette.background.paper,
    borderColor: theme.palette.text.primary,
    '&:hover': {
      background: theme.palette.text.primary,
      color: theme.palette.background.paper,
    },
  },
  controlLabel: {
    ...theme.typography.caption,
    marginTop: 4,
    lineHeight: 1.2,
  },
  controlIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  recordingBar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  recordingLive: {
    ...theme.typography.caption,
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: endRed(theme),
    fontWeight: 600,
  },
  recordingDot: {
    fontSize: 10,
  },
  recordingStopped: {
    ...theme.typography.caption,
    color: secondaryText(theme),
  },
  stopRecording: {
    ...theme.typography.caption,
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    transition: 'background 120ms ease, color 120ms ease',
    '&:hover:not(:disabled)': {
      background: theme.palette.action.hover,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.text.primary}`,
      outlineOffset: 2,
    },
    '&:disabled': {
      cursor: 'default',
      color: secondaryText(theme),
      opacity: 0.7,
    },
  },
  recordingError: {
    ...theme.typography.caption,
    color: endRed(theme),
    flexBasis: '100%',
    textAlign: 'center',
  },
  endButton: {
    width: END_BUTTON_DIAMETER,
    height: END_BUTTON_DIAMETER,
    borderRadius: '50%',
    border: 'none',
    background: endRed(theme),
    color: endInk(theme),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
    boxShadow: `0 4px 12px ${alpha(endRed(theme), 0.35)}`,
    transition: 'filter 120ms ease, transform 80ms ease',
    '&:hover': {
      filter: 'brightness(1.1)',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.text.primary}`,
      outlineOffset: 2,
    },
  },
}));

export type InCallControlsVariant = 'surface' | 'onGround';

/**
 * Only controls backed by a real capability on the browser call leg are
 * rendered. The Twilio Voice SDK exposes no hold for this leg (hold needs a
 * server-side conference) and speaker choice is a `setSinkId` output-device
 * concern that browsers do not universally support, so neither ships as a
 * toggle that does nothing. Every control's `aria-pressed` reflects its own
 * state.
 */
export interface IInCallControlsProps {
  variant?: InCallControlsVariant;
  isMuted: boolean;
  isKeypadOpen: boolean;

  onMuteToggle: () => void;
  onKeypadToggle: () => void;
  onEndCall: () => void;

  onAddPerson?: () => void;
  addDisabled?: boolean;

  recordingState?: TRecordingControlState;
  onStopRecording?: () => void;
}

const ControlButton: React.FC<{
  active: boolean;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  variant: InCallControlsVariant;
  ariaLabel?: string;
  toggle?: boolean;
  disabled?: boolean;
  opensDialog?: boolean;
}> = ({ active, label, icon, onClick, variant, ariaLabel, toggle = true, disabled = false, opensDialog = false }) => {
  const classes = useInCallControlsStyles();
  const onGround = variant === 'onGround';
  const base = onGround ? classes.dockButton : classes.control;
  const activeClass = onGround ? classes.dockButtonOn : classes.controlActive;
  const labelClass = onGround ? classes.dockLabel : classes.controlLabel;
  return (
    <button
      type="button"
      aria-pressed={toggle ? active : undefined}
      aria-haspopup={opensDialog ? 'dialog' : undefined}
      aria-label={ariaLabel ?? label}
      disabled={disabled}
      onClick={onClick}
      className={[base, active ? activeClass : ''].filter(Boolean).join(' ')}
    >
      <span className={classes.controlIcon}>{icon}</span>
      <span className={labelClass}>{label}</span>
    </button>
  );
};

const RecordingBar: React.FC<{
  state: TRecordingControlState;
  onStop?: () => void;
}> = ({ state, onStop }) => {
  const classes = useInCallControlsStyles();
  if (state === 'hidden') {
    return null;
  }
  const busy = state === 'stopping';
  const stopped = state === 'stopped';
  return (
    <div className={classes.recordingBar} role="status">
      {stopped ? (
        <span className={classes.recordingStopped}>{RECORDING_STOPPED_LABEL}</span>
      ) : (
        <span className={classes.recordingLive}>
          <FiberManualRecordIcon className={classes.recordingDot} fontSize="inherit" />
          {RECORDING_ACTIVE_LABEL}
        </span>
      )}
      {stopped ? null : (
        <button
          type="button"
          className={classes.stopRecording}
          onClick={onStop}
          disabled={busy || !onStop}
          aria-busy={busy}
          aria-label={state === 'failed' ? STOP_RETRY_LABEL : STOP_RECORDING_LABEL}
        >
          <StopCircleIcon fontSize="small" />
          {(() => {
            if (busy) {
              return STOPPING_RECORDING_LABEL;
            }
            return state === 'failed' ? STOP_RETRY_LABEL : STOP_RECORDING_LABEL;
          })()}
        </button>
      )}
      {state === 'failed' ? (
        <span className={classes.recordingError} role="alert">{STOP_FAILED_TEXT}</span>
      ) : null}
    </div>
  );
};

const InCallControls: React.FC<IInCallControlsProps> = ({
  variant = 'surface',
  isMuted,
  isKeypadOpen,
  onMuteToggle,
  onKeypadToggle,
  onEndCall,
  onAddPerson,
  addDisabled = false,
  recordingState = 'hidden',
  onStopRecording,
}) => {
  const classes = useInCallControlsStyles();
  const onGround = variant === 'onGround';
  const iconSize = onGround ? 'inherit' : 'small';
  const row = (
    <div className={onGround ? classes.dockRow : classes.grid}>
      <ControlButton
        active={isMuted}
        label="Mute"
        variant={variant}
        icon={isMuted ? <MicOffIcon fontSize={iconSize} /> : <MicIcon fontSize={iconSize} />}
        onClick={onMuteToggle}
      />
      <ControlButton
        active={isKeypadOpen}
        label="Keypad"
        variant={variant}
        icon={<DialpadIcon fontSize={iconSize} />}
        onClick={onKeypadToggle}
      />
      {onAddPerson ? (
        <ControlButton
          active={false}
          toggle={false}
          label="Add"
          ariaLabel="Add a person to this call"
          opensDialog
          variant={variant}
          disabled={addDisabled}
          icon={<PersonAddAlt1Icon fontSize={iconSize} />}
          onClick={onAddPerson}
        />
      ) : null}
    </div>
  );

  if (onGround) {
    return (
      <div className={classes.dock}>
        {row}
        <button type="button" onClick={onEndCall} className={classes.endWide}>
          <CallEndIcon fontSize="inherit" aria-hidden />
          End call
        </button>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <RecordingBar state={recordingState} onStop={onStopRecording} />
      {row}

      <button
        type="button"
        aria-label="End call"
        onClick={onEndCall}
        className={classes.endButton}
      >
        <CallEndIcon fontSize="medium" />
      </button>
    </div>
  );
};

export default InCallControls;
